// Pure APS rules shared by web and api (no zod: safe in route entry chunks).
// Types come from ./aps (type-only).
import type { KitOrder } from "./aps";

/** Coverage colour (spec): green 100 %, amber 80–99.99 %, red below 80 %. */
export type CoverageTone = "success" | "warning" | "danger";
export function coverageTone(pct: number): CoverageTone {
  if (pct >= 100) return "success";
  if (pct >= 80) return "warning";
  return "danger";
}

/** Children of each order (the kit tree), roots first in start order. */
export interface TreeNode {
  id: string;
  number: string;
  start_date: string;
  parent_order_id?: string | null | undefined;
}

export function kitTree<T extends TreeNode = KitOrder>(orders: readonly T[]): { order: T; depth: number }[] {
  const ids = new Set(orders.map((o) => o.id));
  const children = new Map<string, T[]>();
  const roots: T[] = [];
  for (const o of orders) {
    if (o.parent_order_id && ids.has(o.parent_order_id)) {
      const list = children.get(o.parent_order_id) ?? [];
      list.push(o);
      children.set(o.parent_order_id, list);
    } else roots.push(o);
  }
  const byStart = (a: T, b: T) => (a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : a.number < b.number ? -1 : 1);
  const out: { order: T; depth: number }[] = [];
  const seen = new Set<string>();
  const walk = (o: T, depth: number) => {
    if (seen.has(o.id)) return;
    seen.add(o.id);
    out.push({ order: o, depth });
    for (const c of (children.get(o.id) ?? []).sort(byStart)) walk(c, depth + 1);
  };
  for (const r of roots.sort(byStart)) walk(r, 0);
  // Members left out (a cycle the database would refuse) still show, flat.
  for (const o of orders) if (!seen.has(o.id)) walk(o, 0);
  return out;
}

/** Subtree of an order (it and its descendants), as a kit order move shifts it. */
export function subtreeIds(orders: readonly TreeNode[], rootId: string): Set<string> {
  const out = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const o of orders) {
      if (o.parent_order_id && out.has(o.parent_order_id) && !out.has(o.id)) {
        out.add(o.id);
        grew = true;
      }
    }
  }
  return out;
}

/** Load tone (aps.md §6): ≤ 85 % green, ≤ 100 % amber, above red. */
export function loadTone(loadMinutes: number, capacityMinutes: number): CoverageTone {
  if (capacityMinutes <= 0) return loadMinutes > 0 ? "danger" : "success";
  const pct = (loadMinutes / capacityMinutes) * 100;
  if (pct <= 85) return "success";
  if (pct <= 100) return "warning";
  return "danger";
}

/** Whole days from a to b (ISO dates, b − a). */
export function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** ISO date + n days. */
export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Monday of the ISO week of a date. */
export function mondayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  return addDays(iso, -dow);
}

/**
 * End-of-day balance for every day of the window from the opening balance and
 * the days with events (the component strip).
 */
export function dailyBalances(
  from: string,
  days: number,
  opening: number,
  events: readonly { date: string; supply: number | string; demand: number | string; balance: number | string }[],
): { date: string; balance: number; supply: number; demand: number }[] {
  const byDate = new Map(events.map((e) => [e.date, e]));
  const out: { date: string; balance: number; supply: number; demand: number }[] = [];
  let balance = opening;
  for (let i = 0; i < days; i++) {
    const date = addDays(from, i);
    const e = byDate.get(date);
    if (e) balance = Number(e.balance);
    out.push({ date, balance, supply: e ? Number(e.supply) : 0, demand: e ? Number(e.demand) : 0 });
  }
  return out;
}
